import { useState, useRef } from 'react';
import { Upload, X, FileSpreadsheet, CheckCircle, AlertCircle } from 'lucide-react';
import { useImportProducts } from '../hooks/useProducts.js';

const MAX_SIZE_BYTES = 20 * 1024 * 1024; // 20 Mo

export default function ProductsUploadModal({ onClose }) {
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [validationError, setValidationError] = useState('');
  const fileInputRef = useRef(null);

  const { mutate: importProducts, isPending, isSuccess, isError, error, data: importResult, reset } = useImportProducts();

  function validateAndSet(f) {
    setValidationError('');
    reset();
    if (!f) return;
    if (!f.name.endsWith('.xlsx')) {
      setValidationError('Le fichier doit être au format .xlsx');
      return;
    }
    if (f.size > MAX_SIZE_BYTES) {
      setValidationError('Le fichier dépasse la limite de 20 Mo');
      return;
    }
    setFile(f);
  }

  function handleDrop(e) {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    validateAndSet(f);
  }

  function handleFileChange(e) {
    validateAndSet(e.target.files?.[0]);
  }

  function handleImport() {
    if (!file || isPending) return;
    importProducts(file);
  }

  function handleClose() {
    if (isPending) return;
    onClose();
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40"
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-lg mx-auto sm:mx-4 p-5 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-slate-900">Importer un inventaire</h2>
          <button
            type="button"
            onClick={handleClose}
            disabled={isPending}
            className="p-1 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 disabled:opacity-40"
            aria-label="Fermer"
          >
            <X size={18} />
          </button>
        </div>

        {/* Succès */}
        {isSuccess && importResult && (
          <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 space-y-1">
            <div className="flex items-center gap-2 text-green-700 font-medium text-sm">
              <CheckCircle size={16} /> Import terminé
            </div>
            <ul className="text-sm text-green-700 space-y-0.5 pl-1">
              <li>{importResult.inserted ?? 0} produits ajoutés</li>
              <li>{importResult.updated ?? 0} produits mis à jour</li>
              <li>{importResult.skipped ?? 0} lignes ignorées</li>
              {(importResult.errors_count ?? 0) > 0 && (
                <li className="text-orange-600">{importResult.errors_count} erreurs</li>
              )}
              {importResult.duration_ms && (
                <li className="text-green-500 text-xs">Durée : {(importResult.duration_ms / 1000).toFixed(1)} s</li>
              )}
            </ul>
            <button
              type="button"
              onClick={handleClose}
              className="mt-2 w-full py-1.5 text-sm font-medium bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              Fermer
            </button>
          </div>
        )}

        {/* Erreur */}
        {isError && (
          <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3">
            <div className="flex items-center gap-2 text-red-700 font-medium text-sm">
              <AlertCircle size={16} /> Erreur lors de l'import
            </div>
            <p className="text-sm text-red-600 mt-1">{error?.message}</p>
          </div>
        )}

        {/* Zone upload — masquée après succès */}
        {!isSuccess && (
          <>
            {/* Drop zone */}
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`relative border-2 border-dashed rounded-xl flex flex-col items-center justify-center gap-3 py-10 cursor-pointer transition-colors ${
                dragOver
                  ? 'border-blue-400 bg-blue-50'
                  : file
                  ? 'border-green-300 bg-green-50'
                  : 'border-slate-200 hover:border-slate-300 bg-slate-50'
              }`}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx"
                className="sr-only"
                onChange={handleFileChange}
                aria-label="Sélectionner un fichier .xlsx"
              />
              {file ? (
                <>
                  <FileSpreadsheet size={28} className="text-green-500" />
                  <div className="text-center">
                    <p className="text-sm font-medium text-slate-800">{file.name}</p>
                    <p className="text-xs text-slate-500">{(file.size / 1024 / 1024).toFixed(2)} Mo</p>
                  </div>
                </>
              ) : (
                <>
                  <Upload size={28} className="text-slate-400" />
                  <div className="text-center">
                    <p className="text-sm text-slate-700">Glisse le fichier ici</p>
                    <p className="text-xs text-slate-500">ou clique pour choisir</p>
                  </div>
                  <p className="text-xs text-slate-400">.xlsx uniquement · max 20 Mo</p>
                </>
              )}
            </div>

            {validationError && (
              <p className="text-sm text-red-600 flex items-center gap-1.5">
                <AlertCircle size={14} /> {validationError}
              </p>
            )}

            {/* Pendant l'upload */}
            {isPending && (
              <div className="text-center text-sm text-slate-600 animate-pulse">
                Import en cours… (peut prendre 1-2 min)
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleClose}
                disabled={isPending}
                className="flex-1 py-2 text-sm rounded-xl border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-40"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleImport}
                disabled={!file || isPending || Boolean(validationError)}
                className="flex-1 py-2 text-sm rounded-xl bg-slate-900 text-white font-medium hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {isPending ? 'Import…' : 'Importer'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
